import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'auto-advanced-offer-set-configuration',
  templateUrl: './offer-set-configuration.component.html',
  styleUrls: ['./offer-set-configuration.component.scss'],
  standalone: false
})
export class AdvancedAdConfigurationComponent implements OnInit, OnChanges, OnDestroy {
  private static readonly DEFAULT_DISCLOSURE_SET_ID = 1;
  private static readonly STANDARD_PLUS_DOC_FEE_DISCLOSURE_SET_ID = 3;
  private static readonly HONDA_OEM_DISCLOSURE_SET_ID = 4;
  private static readonly MERCEDES_BENZ_OEM_DISCLOSURE_SET_NAME = 'Mercedes-Benz OEM';

  @Input() data: any | null = null;
  @Output() dataEmitter = new EventEmitter<UntypedFormGroup>();
  form: UntypedFormGroup;
  disclosureSetOptions: { label: string; value: number }[] = [];
  disclosureSetTooltip = '';
  private offerSetAdvancedSettings: any;

  private destroy$ = new Subject<void>();

  constructor(private formBuilder: UntypedFormBuilder) {}

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      accountOfferSetRefresh: [false],
      customTitleDisplayOverride: [false],
      newOfferSetCustomTitleOverrideText: [''],
      usedOfferSetCustomTitleOverrideText: [''],
      cpoOfferSetCustomTitleOverrideText: [''],
      minimizeDisclaimer: [false],
      newMainCtaText: [''],
      usedMainCtaText: [''],
      disclosureSet: [0],
      enhancedDisclosure: [false],
      ncsUseLatestSpecialsUx: [false],
      sharedOfferSetsEnabled: [false],
      enhancedDisclosureEnabled: [false]
    });

    this.form.get('enhancedDisclosure')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((enabled: boolean) => {
        this.offerSetAdvancedSettings = { ...(this.offerSetAdvancedSettings || {}), enhancedDisclosure: enabled };
        this.disclosureSetOptions = this.buildDisclosureSetOptions(this.offerSetAdvancedSettings);

        const currentValue = this.form.get('disclosureSet')?.value;
        const exists = this.disclosureSetOptions.some(option => option.value === currentValue);

        if (!exists) {
          this.form.patchValue(
            { disclosureSet: AdvancedAdConfigurationComponent.DEFAULT_DISCLOSURE_SET_ID },
            { emitEvent: false }
          );
        }

        this.updateDisclosureSetTooltip();
      });

    this.dataEmitter.emit(this.form);
    this.patchFromInput(this.data);

    this.form.get('disclosureSet')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.updateDisclosureSetTooltip();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data']) {
      this.patchFromInput(changes['data'].currentValue);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private patchFromInput(offerSetAdvancedSettings: any): void {
    if (offerSetAdvancedSettings && this.form) {
      this.offerSetAdvancedSettings = offerSetAdvancedSettings;

      // Build options
      this.disclosureSetOptions = this.buildDisclosureSetOptions(offerSetAdvancedSettings);

      // Selected disclosure set
      let selectedDisclosureSet = 0;
      if (Number.isFinite(offerSetAdvancedSettings.disclosureSet)) {
        selectedDisclosureSet = Number(offerSetAdvancedSettings.disclosureSet);
      }

      this.form.patchValue(
        {
          accountOfferSetRefresh: !!offerSetAdvancedSettings.accountOfferSetRefresh,
          customTitleDisplayOverride: !!offerSetAdvancedSettings.customTitleDisplayOverride,
          newOfferSetCustomTitleOverrideText: offerSetAdvancedSettings.newOfferSetCustomTitleOverrideText || '',
          usedOfferSetCustomTitleOverrideText: offerSetAdvancedSettings.usedOfferSetCustomTitleOverrideText || '',
          cpoOfferSetCustomTitleOverrideText: offerSetAdvancedSettings.cpoOfferSetCustomTitleOverrideText || '',
          minimizeDisclaimer: !!offerSetAdvancedSettings.minimizeDisclaimer,
          newMainCtaText: offerSetAdvancedSettings.newMainCtaText || '',
          usedMainCtaText: offerSetAdvancedSettings.usedMainCtaText || '',
          disclosureSet: selectedDisclosureSet,
          enhancedDisclosure: !!offerSetAdvancedSettings.enhancedDisclosure,
          sharedOfferSetsEnabled: !!offerSetAdvancedSettings.sharedOfferSetsEnabled,
        },
        { emitEvent: false }
      );

      this.updateDisclosureSetTooltip();

      this.form.markAsPristine();
    }
  }

  private buildDisclosureSetOptions(offerSetAdvancedSettings: any): { label: string; value: number }[] {
    let options: { label: string; value: number }[] = [];
    const sets = offerSetAdvancedSettings?.disclosureSets;

    if (Array.isArray(sets)) {
      const enhancedEnabled = !!offerSetAdvancedSettings?.enhancedDisclosure;
      options = sets
        .filter((disclosureSet: any) => disclosureSet && Number.isFinite(disclosureSet.id))
        .filter((disclosureSet: any) => {
          if (enhancedEnabled) {
            return disclosureSet?.name !== AdvancedAdConfigurationComponent.MERCEDES_BENZ_OEM_DISCLOSURE_SET_NAME;
          }
          return true;
        })
        .map((disclosureSet: any) => {
          let label = String(disclosureSet.id);
          if (disclosureSet?.name?.length) {
            label = disclosureSet.name;
          }
          return { label, value: Number(disclosureSet.id) };
        });
    }

    return options;
  }

  private updateDisclosureSetTooltip(): void {
    const selectedValue = this.form.get('disclosureSet')?.value;

    let tooltip = '';

    const selectedOption = this.disclosureSetOptions.find(option => option.value === selectedValue);

    if (selectedOption?.value === AdvancedAdConfigurationComponent.STANDARD_PLUS_DOC_FEE_DISCLOSURE_SET_ID) {
      tooltip = 'Includes the Doc Fee from Ad Types > Fees in the disclosure, even if set to $0.';
    } else if (selectedOption?.value === AdvancedAdConfigurationComponent.HONDA_OEM_DISCLOSURE_SET_ID) {
      tooltip = 'Removes any "rebate" terminology from the disclosure.';
    }

    this.disclosureSetTooltip = tooltip;
  }

}
